import { useState, useRef, useEffect } from "react";
import { API } from "../../lib/api";
import { PageHeader } from "../../components/shared";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import { Sparkles, Send, Loader2, User } from "lucide-react";

const SUGGESTIONS = ["What is the quorum for an AGM?", "How are sinking fund and repair fund different?", "Can the committee change parking allotment?", "What is the process for society redevelopment?"];

export default function AskMitra() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [provider, setProvider] = useState("openai");
  const endRef = useRef();
  const sessionRef = useRef(`ask-${crypto.randomUUID()}`);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  const ask = async (q) => {
    const question = (q || input).trim();
    if (!question) return;
    setMessages((m) => [...m, { role: "user", text: question }]);
    setInput(""); setLoading(true);
    try {
      const response = await fetch(`${API}/tools/ask/stream`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question, provider, session_id: sessionRef.current }),
      });
      if (!response.ok || !response.body) throw new Error("Could not start the AI response");
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let answer = "";
      let disclaimer = "";
      setMessages((m) => [...m, { role: "assistant", text: "" }]);
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop() || "";
        events.forEach((event) => {
          const line = event.split("\n").find((item) => item.startsWith("data: "));
          if (!line) return;
          const payload = JSON.parse(line.slice(6));
          if (payload.type === "token") answer += payload.content;
          if (payload.type === "done") disclaimer = payload.disclaimer || "";
          if (payload.type === "error") throw new Error(payload.detail);
        });
        setMessages((m) => [...m.slice(0, -1), { role: "assistant", text: answer, disclaimer }]);
      }
    } catch (error) { toast.error(error.message || "Could not get an answer"); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-3xl mx-auto flex flex-col h-[calc(100vh-8rem)]">
      <PageHeader title="Ask SocietyMitra" subtitle="Housing society knowledge assistant — general information, not legal advice." />
      <div className="mb-4 flex items-center gap-2">
        <label className="text-sm font-medium" htmlFor="ask-provider">AI provider</label>
        <select
          data-testid="ask-provider-select"
          id="ask-provider"
          onChange={(event) => setProvider(event.target.value)}
          value={provider}
        >
          <option value="openai">ChatGPT · GPT-5.4</option>
          <option value="gemini">Gemini · 3.1 Pro</option>
        </select>
      </div>
      <div className="flex-1 overflow-y-auto space-y-4 pb-4">
        {messages.length === 0 && (
          <div className="bg-card border rounded-xl p-6 text-center">
            <Sparkles className="w-8 h-8 text-secondary mx-auto" />
            <p className="text-muted-foreground mt-3">Ask anything about housing society management.</p>
            <div className="flex flex-wrap gap-2 justify-center mt-4">
              {SUGGESTIONS.map((s, index) => <button key={s} onClick={() => ask(s)} className="text-sm border rounded-full px-3 py-1.5 hover:bg-muted" data-testid={`ask-suggestion-${index}`}>{s}</button>)}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`}>
            {m.role === "assistant" && <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center shrink-0"><Sparkles className="w-4 h-4" /></div>}
            <div className={`rounded-xl p-4 max-w-[80%] ${m.role === "user" ? "bg-primary text-white" : "bg-card border"}`}>
              <div className="whitespace-pre-wrap text-sm leading-relaxed">{m.text}</div>
              {m.disclaimer && <div className="mt-2 text-xs opacity-70 italic border-t pt-2">{m.disclaimer}</div>}
            </div>
            {m.role === "user" && <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0"><User className="w-4 h-4" /></div>}
          </div>
        ))}
        {loading && <div className="flex gap-3"><div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center"><Sparkles className="w-4 h-4" /></div><div className="bg-card border rounded-xl p-4"><Loader2 className="w-4 h-4 animate-spin" /></div></div>}
        <div ref={endRef} />
      </div>
      <div className="flex gap-2 border-t pt-3">
        <Textarea rows={1} value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(); } }}
          placeholder="Type your question..." className="resize-none" data-testid="ask-input" />
        <Button onClick={() => ask()} disabled={loading} className="rounded-full h-auto px-4" data-testid="ask-send"><Send className="w-4 h-4" /></Button>
      </div>
    </div>
  );
}
